import { ArrowRight, ArrowUpRight } from "lucide-react";
import { BOOKING_URL, CONTACT_EMAIL } from "@/config";
import GearMark from "@/components/GearMark";
import { Reveal } from "@/components/Reveal";

export function PrimaryCTA({ children = "Get a demo", testid = "cta-get-a-demo-btn", className = "" }) {
  return (
    <a
      data-testid={testid}
      href={BOOKING_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`group inline-flex items-center justify-center gap-2.5 rounded-full bg-vermillion px-7 py-3.5 text-sm font-semibold tracking-wide text-white transition-all duration-300 hover:bg-vermillion-deep hover:shadow-[0_18px_44px_-14px_rgba(214,53,10,0.65)] active:scale-[0.98] ${className}`}
    >
      {children}
      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
    </a>
  );
}

export function SecondaryCTA({ children = "Book a consultation", testid = "cta-book-consultation-btn", className = "" }) {
  return (
    <a
      data-testid={testid}
      href={BOOKING_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`group inline-flex items-center justify-center gap-2.5 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-semibold tracking-wide text-ink transition-all duration-300 hover:border-ink hover:bg-ink hover:text-paper active:scale-[0.98] ${className}`}
    >
      {children}
      <ArrowUpRight
        size={16}
        className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
      />
    </a>
  );
}

export function CTABand() {
  return (
    <section data-testid="cta-band" className="relative overflow-hidden bg-coal text-white">
      <div className="pointer-events-none absolute inset-y-0 left-0 w-px bg-bluerule/50" />
      <GearMark
        size={560}
        className="pointer-events-none absolute -right-28 -top-28 animate-spin-slower opacity-[0.09]"
      />
      <div className="container-t relative py-24 lg:py-28">
        <Reveal>
          <p className="eyebrow text-bluerule">
            <span className="eyebrow-mark" />
            Get started
          </p>
          <h2 className="mt-6 max-w-2xl font-display text-4xl font-extrabold leading-[1.08] tracking-[-0.02em] lg:text-[3.4rem]">
            See it run <span className="font-accent italic font-medium text-vermillion">your</span> pipeline.
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-white/70 lg:text-lg">
            Bring one live channel or alliance problem to the call — leave with a walkthrough and a plan
            built around it.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <PrimaryCTA testid="cta-band-book-consultation-btn" className="!px-8 !py-4">
              Book a consultation
            </PrimaryCTA>
            <a
              data-testid="cta-band-get-a-demo-btn"
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold tracking-wide text-white transition-all duration-300 hover:border-white/60 hover:bg-white/10 active:scale-[0.98]"
            >
              Get a demo
              <ArrowRight size={16} />
            </a>
          </div>
          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
            or email{" "}
            <a
              data-testid="cta-band-email-link"
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-bluerule underline-offset-4 transition-colors hover:text-white hover:underline"
            >
              {CONTACT_EMAIL}
            </a>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
